import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Dropdown,
  Empty,
  Input,
  Select,
  Skeleton,
  Tag,
  Tooltip,
} from 'antd';
import type { MenuProps, TablePaginationConfig } from 'antd';
import type { SorterResult } from 'antd/es/table/interface';
import dayjs, { Dayjs } from 'dayjs';
import {
  useSearchTransportMovements,
  useTransportMovements,
} from '@/api/TransportMovements';
import { useDebounce } from '@/hooks';
import { useTranslate } from '@/translations';
import { TransportMovement } from '@/types';
import {
  CustomColumns,
  CustomTable,
  faAngleRight,
  faArrowDown,
  faArrowUp,
  faCaretDown,
  faFileCsv,
  faFileExcel,
  faFilePdf,
  faFileWord,
  faSearch,
  faXmark,
  Icon,
} from '@/ui/components';
import {
  exportToFile,
  type IDownloadTypes,
} from '@/ui/components/CustomTable/exportToFile';
import { FORMAT_DATE_DAYJS, FORMAT_FULL_TIME_DAYJS } from '@/utils/constants';
import {
  DEFAULT_LOAD_SORT,
  EMPTY_LOAD_FILTERS,
  filterLoads,
  formatCollection,
  formatStoredDateTime,
  getDistinctOptions,
  getSearchState,
  getStatusTabs,
  LoadListFilters,
  LoadSort,
  LoadSortField,
  sortLoads,
} from '@/modules/TransportMovement/loadList';
import { NotRecorded } from '@/modules/TransportMovement/components/NotRecorded';
import { LoadStatusPill } from '@/modules/TransportMovement/components/LoadStatusPill';
import { TransportLoadDrawer } from '@/modules/TransportMovement/components/TransportLoadDrawer';
import {
  AppliedFilters,
  AppliedFiltersLabel,
  DrawerSticky,
  DrawerViewport,
  Emphasis,
  FilterRow,
  LoadTableContainer,
  PageSubtitle,
  PageTitle,
  ResultBar,
  ResultCount,
  RowChevron,
  SearchNotice,
  SortLabel,
  StatusTab,
  StatusTabs,
  Toolbar,
  VinMatch,
  VinMatchLabel,
} from './TransportMovementDataGrid.styled';

const DRAWER_WIDTH = 'min(760px, calc(100% - 32px))';
const DRAWER_MIN_HEIGHT = 640;
const SORT_FIELDS: LoadSortField[] = [
  'collectionDate',
  'loadNumber',
  'statusDesc',
  'vehicleCount',
  'fromLocation',
  'providerName',
];

const keepGivenOrder = () => 0;

const NO_LOADS: TransportMovement[] = [];

const highlightMatch = (value: string, term: string) => {
  const start = term ? value.toUpperCase().indexOf(term) : -1;
  if (start < 0) return value;
  return (
    <>
      {value.slice(0, start)}
      <strong>{value.slice(start, start + term.length)}</strong>
      {value.slice(start + term.length)}
    </>
  );
};

const EXPORT_MENU_ITEMS: MenuProps['items'] = [
  { key: 'xlsx', label: 'XLSX', icon: <Icon icon={faFileExcel} /> },
  { key: 'csv', label: 'CSV', icon: <Icon icon={faFileCsv} /> },
  { key: 'docx', label: 'DOCX', icon: <Icon icon={faFileWord} /> },
  { key: 'pdf', label: 'PDF', icon: <Icon icon={faFilePdf} /> },
];

export const TransportDataGrid: FC = () => {
  const { t } = useTranslate();
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
  const [filters, setFilters] = useState<LoadListFilters>(EMPTY_LOAD_FILTERS);
  const [sort, setSort] = useState<LoadSort>(DEFAULT_LOAD_SORT);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [openLoadId, setOpenLoadId] = useState<number | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const searchState = getSearchState(filters.search);
  const searchTerm = useDebounce(
    searchState.isSearching ? searchState.term : '',
    400,
  );
  const isSearchActive = !!searchTerm;
  const searchNotice =
    searchState.hint === 'minLength'
      ? t('tsSearchMinLength')
      : searchState.hint === 'lettersAndDigits'
        ? t('tsSearchLettersAndDigits')
        : searchState.isSearching
          ? t('tsSearchingAllDates')
          : null;

  const dateQuery = useTransportMovements(selectedDate.format('YYYY-MM-DD'), {
    enabled: !isSearchActive,
  });
  const searchQuery = useSearchTransportMovements(searchTerm, {
    enabled: isSearchActive,
  });
  const activeQuery = isSearchActive ? searchQuery : dateQuery;
  const loads = activeQuery.data?.entities ?? NO_LOADS;
  const loading = activeQuery.isFetching;
  const loadError = activeQuery.isError;

  const { withoutStatus, visible } = useMemo(
    () => filterLoads(loads, filters),
    [loads, filters],
  );
  const statusTabs = useMemo(
    () => getStatusTabs(loads, withoutStatus, filters.status),
    [loads, withoutStatus, filters.status],
  );
  const sortedLoads = useMemo(() => sortLoads(visible, sort), [visible, sort]);
  const siteOptions = useMemo(
    () => getDistinctOptions(loads, 'fromLocation'),
    [loads],
  );
  const transporterOptions = useMemo(
    () => getDistinctOptions(loads, 'providerName'),
    [loads],
  );

  const pageCount = Math.max(
    1,
    Math.ceil(sortedLoads.length / pagination.pageSize),
  );
  const currentPage = Math.min(pagination.current, pageCount);

  const openLoad = loads.find((load) => load.loadId === openLoadId) ?? null;

  const isToday = selectedDate.isSame(dayjs(), 'day');
  const hasActiveFilters =
    !isToday ||
    !!filters.search.trim() ||
    filters.sites.length > 0 ||
    filters.transporters.length > 0 ||
    !!filters.status;

  const resetToFirstPage = () =>
    setPagination((previous) => ({ ...previous, current: 1 }));

  const updateFilters = (patch: Partial<LoadListFilters>) => {
    setFilters((previous) => ({ ...previous, ...patch }));
    resetToFirstPage();
  };

  const clearFilters = () => {
    setFilters(EMPTY_LOAD_FILTERS);
    resetToFirstPage();
    if (!isToday) {
      setDrawerOpen(false);
      setSelectedDate(dayjs());
    }
  };

  const handleDateChange = (date: Dayjs | null) => {
    setDrawerOpen(false);
    setSelectedDate(date ?? dayjs());
    resetToFirstPage();
  };

  const appliedFilters: { key: string; label: string; remove: () => void }[] = [
    ...(searchState.isSearching
      ? [
          {
            key: 'search',
            label: `${t('search')}: ${searchState.term}`,
            remove: () => updateFilters({ search: '' }),
          },
        ]
      : []),
    ...(filters.status
      ? [
          {
            key: 'status',
            label: `${t('tsColStatus')}: ${filters.status}`,
            remove: () => updateFilters({ status: null }),
          },
        ]
      : []),
    ...(!isToday && !searchState.isSearching
      ? [
          {
            key: 'date',
            label: `${t('tsDateFilter')}: ${selectedDate.format(FORMAT_DATE_DAYJS)}`,
            remove: () => handleDateChange(dayjs()),
          },
        ]
      : []),
    ...filters.sites.map((site) => ({
      key: `site-${site}`,
      label: `${t('tsSiteFilter')}: ${site}`,
      remove: () =>
        updateFilters({ sites: filters.sites.filter((s) => s !== site) }),
    })),
    ...filters.transporters.map((transporter) => ({
      key: `transporter-${transporter}`,
      label: `${t('tsTransporterFilter')}: ${transporter}`,
      remove: () =>
        updateFilters({
          transporters: filters.transporters.filter(
            (value) => value !== transporter,
          ),
        }),
    })),
  ];

  const selectedSummary = (selected: { label?: React.ReactNode }[]) =>
    selected.length === 1
      ? selected[0].label
      : t('tsSelectedCount', { count: selected.length });

  const openLoadDetails = (load: TransportMovement) => {
    setOpenLoadId(load.loadId);
    setDrawerOpen(true);
  };

  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  const handleDrawerOpenChange = (isOpen: boolean) => {
    if (isOpen || openLoadId === null) return;
    const row = tableContainerRef.current?.querySelector<HTMLElement>(
      `tr[data-row-key="${openLoadId}"]`,
    );
    row?.focus();
  };

  const handleTableChange = (
    nextPagination: TablePaginationConfig,
    _filters: unknown,
    sorter: SorterResult<TransportMovement> | SorterResult<TransportMovement>[],
    { action }: { action: string },
  ) => {
    if (action === 'sort') {
      const { field, order } = Array.isArray(sorter) ? sorter[0] : sorter;
      setSort(
        field && order
          ? { field: field as LoadSortField, order }
          : DEFAULT_LOAD_SORT,
      );
      resetToFirstPage();
      return;
    }
    if (action === 'paginate') {
      setPagination({
        current: nextPagination.current ?? 1,
        pageSize: nextPagination.pageSize ?? pagination.pageSize,
      });
    }
  };

  const sortOptions = useMemo(() => {
    const labels: Record<LoadSortField, string> = {
      collectionDate: t('tsSortCollectionTime'),
      loadNumber: t('tsSortLoadNumber'),
      statusDesc: t('tsSortStatus'),
      vehicleCount: t('tsSortVehicles'),
      fromLocation: t('tsSortFrom'),
      providerName: t('tsSortCarrier'),
    };
    return SORT_FIELDS.map((field) => ({ value: field, label: labels[field] }));
  }, [t]);
  const sortDirectionLabel =
    sort.order === 'ascend' ? t('tsSortAscending') : t('tsSortDescending');

  const collectionLabels = useMemo(
    () => ({
      today: t('tsToday'),
      tomorrow: t('tsTomorrow'),
      yesterday: t('tsYesterday'),
    }),
    [t],
  );

  const columns: CustomColumns<TransportMovement> = useMemo(() => {
    const sortable = (field: LoadSortField) => ({
      key: field,
      dataIndex: field,
      sorter: keepGivenOrder,
      sortOrder: sort.field === field ? sort.order : null,
      align: 'center' as const,
    });

    return [
      {
        ...sortable('loadNumber'),
        title: t('tsColLoads'),
        width: 170,
        render: (value: string, record) => (
          <>
            <Emphasis>{value}</Emphasis>
            {record.matchedVin && (
              <VinMatch
                aria-label={t('tsVinMatch', { vin: record.matchedVin })}
              >
                <VinMatchLabel>{t('tsVinMatchLabel')}</VinMatchLabel>
                {highlightMatch(record.matchedVin, searchTerm)}
              </VinMatch>
            )}
          </>
        ),
      },
      {
        ...sortable('statusDesc'),
        title: t('tsColStatus'),
        width: 190,
        render: (value: string | null) => <LoadStatusPill status={value} />,
      },
      {
        ...sortable('vehicleCount'),
        title: t('tsColVehicles'),
        width: 100,
        render: (value: number | null) => value ?? <NotRecorded />,
      },
      {
        ...sortable('collectionDate'),
        title: t('tsColCollection'),
        width: 170,
        render: (value: string | null) => {
          const formatted = formatCollection(value, collectionLabels);
          return formatted ? (
            <Tooltip
              title={formatStoredDateTime(value, FORMAT_FULL_TIME_DAYJS)}
            >
              <Emphasis>{formatted}</Emphasis>
            </Tooltip>
          ) : (
            <NotRecorded />
          );
        },
      },
      {
        ...sortable('fromLocation'),
        title: t('tsColFrom'),
        ellipsis: { showTitle: true },
        render: (value: string | null) => value || <NotRecorded />,
      },
      {
        ...sortable('providerName'),
        title: t('tsColCarrier'),
        ellipsis: { showTitle: true },
        render: (value: string | null) => value || <NotRecorded />,
      },
      {
        key: 'open',
        dataIndex: 'loadId',
        width: 40,
        align: 'center',
        render: () => (
          <RowChevron aria-hidden>
            <Icon icon={faAngleRight} />
          </RowChevron>
        ),
      },
    ];
  }, [t, sort, collectionLabels, searchTerm]);

  const exportColumns: CustomColumns<TransportMovement> = useMemo(
    () => [
      { dataIndex: 'loadNumber', title: t('tsColLoads') },
      { dataIndex: 'statusDesc', title: t('tsColStatus') },
      { dataIndex: 'vehicleCount', title: t('tsColVehicles') },
      {
        dataIndex: 'collectionDate',
        title: t('tsColCollection'),
        exportRender: (value) =>
          formatStoredDateTime(value, FORMAT_FULL_TIME_DAYJS) ?? '',
      },
      { dataIndex: 'fromLocation', title: t('tsColFrom') },
      { dataIndex: 'toLocation', title: t('tsDestination') },
      { dataIndex: 'providerName', title: t('tsColCarrier') },
    ],
    [t],
  );

  const exportMenu: MenuProps = {
    items: EXPORT_MENU_ITEMS,
    onClick: ({ key }) =>
      exportToFile(
        isSearchActive
          ? `transport-summary-search-${searchTerm}`
          : `transport-summary-${selectedDate.format('YYYY-MM-DD')}`,
        sortedLoads,
        exportColumns,
        key as IDownloadTypes,
      ),
  };

  const emptyText = loading ? (
    <Skeleton active title={false} paragraph={{ rows: 5 }} />
  ) : loadError ? (
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description={t('tsLoadError')}
    />
  ) : (
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description={
        loads.length > 0
          ? t('tsEmptyNoResults')
          : isSearchActive
            ? t('tsEmptyNoSearchResults', { search: searchTerm })
            : t('tsEmptyNoLoads', {
                date: selectedDate.format(FORMAT_DATE_DAYJS),
              })
      }
    >
      {hasActiveFilters && (
        <Button onClick={clearFilters}>{t('tsClearFilters')}</Button>
      )}
    </Empty>
  );

  return (
    <Card
      style={{
        position: 'relative',
        overflow: 'clip',
        minHeight: drawerOpen ? DRAWER_MIN_HEIGHT : undefined,
      }}
    >
      <PageTitle>{t('transportSummary')}</PageTitle>
      <PageSubtitle>
        {drawerOpen && openLoad
          ? t('tsSelectedLoadDetails', { loadNumber: openLoad.loadNumber })
          : t('tsSubtitle')}
      </PageSubtitle>

      <Toolbar>
        <StatusTabs role="group" aria-label={t('tsStatusTabs')}>
          <StatusTab
            type="button"
            $active={!filters.status}
            aria-pressed={!filters.status}
            onClick={() => updateFilters({ status: null })}
          >
            {t('tsAllStatuses')} {withoutStatus.length}
          </StatusTab>
          {statusTabs.map(({ status, count }) => (
            <StatusTab
              key={status}
              type="button"
              $active={filters.status === status}
              aria-pressed={filters.status === status}
              onClick={() => updateFilters({ status })}
            >
              {status} {count}
            </StatusTab>
          ))}
        </StatusTabs>

        <FilterRow>
          <Input
            className="transport-search"
            size="large"
            allowClear
            value={filters.search}
            onChange={(e) => updateFilters({ search: e.target.value })}
            placeholder={t('tsSearchPlaceholder')}
            aria-label={t('tsSearchPlaceholder')}
            aria-describedby={
              searchNotice ? 'transport-search-notice' : undefined
            }
            status={searchState.hint ? 'warning' : undefined}
            prefix={<Icon icon={faSearch} />}
          />
          <DatePicker
            className="transport-filter"
            size="large"
            prefix={`${t('tsDateFilter')}:`}
            aria-label={t('tsDateFilter')}
            disabled={searchState.isSearching}
            value={selectedDate}
            onChange={handleDateChange}
            suffixIcon={<Icon icon={faCaretDown} />}
            format={(date) =>
              date.isSame(dayjs(), 'day')
                ? t('tsToday')
                : date.format(FORMAT_DATE_DAYJS)
            }
            inputReadOnly
            allowClear={false}
          />
          <Select
            className="transport-filter"
            size="large"
            mode="multiple"
            prefix={`${t('tsSiteFilter')}:`}
            aria-label={t('tsSiteFilter')}
            allowClear
            showSearch
            value={filters.sites}
            onChange={(sites) => updateFilters({ sites })}
            options={siteOptions}
            placeholder={t('tsAllSites')}
            maxTagCount={0}
            maxTagPlaceholder={selectedSummary}
            suffixIcon={<Icon icon={faCaretDown} />}
            popupMatchSelectWidth={false}
          />
          <Select
            className="transport-filter"
            size="large"
            mode="multiple"
            prefix={
              filters.transporters.length
                ? `${t('tsTransporterFilter')}:`
                : t('tsTransporterFilter')
            }
            aria-label={t('tsTransporterFilter')}
            allowClear
            showSearch
            value={filters.transporters}
            onChange={(transporters) => updateFilters({ transporters })}
            options={transporterOptions}
            maxTagCount={0}
            maxTagPlaceholder={selectedSummary}
            suffixIcon={<Icon icon={faCaretDown} />}
            popupMatchSelectWidth={false}
          />
          <Dropdown
            menu={exportMenu}
            disabled={loading || sortedLoads.length === 0}
          >
            <Button className="transport-export" type="primary" size="large">
              {t('tsExportResults')}
            </Button>
          </Dropdown>
        </FilterRow>

        {searchNotice && (
          <SearchNotice id="transport-search-notice" role="status">
            {searchNotice}
          </SearchNotice>
        )}

        {appliedFilters.length > 0 && (
          <AppliedFilters aria-label={t('tsAppliedFilters')}>
            <AppliedFiltersLabel>{t('tsAppliedFilters')}</AppliedFiltersLabel>
            {appliedFilters.map(({ key, label, remove }) => (
              <Tag
                key={key}
                closable
                closeIcon={<Icon icon={faXmark} />}
                onClose={(e) => {
                  e.preventDefault();
                  remove();
                }}
              >
                {label}
              </Tag>
            ))}
            <Button type="link" size="small" onClick={clearFilters}>
              {t('tsClearFilters')}
            </Button>
          </AppliedFilters>
        )}

        <ResultBar>
          <ResultCount aria-live="polite">
            {t('tsLoadCount', { count: sortedLoads.length })}
          </ResultCount>
          <div>
            <SortLabel>{t('tsSortBy')}</SortLabel>
            <Select
              variant="borderless"
              size="small"
              aria-label={t('tsSortBy')}
              value={sort.field}
              onChange={(field) => {
                setSort({ field, order: 'ascend' });
                resetToFirstPage();
              }}
              options={sortOptions}
              popupMatchSelectWidth={false}
            />
            <Tooltip title={sortDirectionLabel}>
              <Button
                type="text"
                size="small"
                aria-label={sortDirectionLabel}
                icon={
                  <Icon
                    icon={sort.order === 'ascend' ? faArrowUp : faArrowDown}
                  />
                }
                onClick={() => {
                  setSort((previous) => ({
                    ...previous,
                    order: previous.order === 'ascend' ? 'descend' : 'ascend',
                  }));
                  resetToFirstPage();
                }}
              />
            </Tooltip>
          </div>
        </ResultBar>

        {loadError && (
          <Alert
            type="error"
            showIcon
            message={t('tsLoadError')}
            action={
              <Button size="small" onClick={() => activeQuery.refetch()}>
                {t('tsRetry')}
              </Button>
            }
          />
        )}
      </Toolbar>

      <LoadTableContainer ref={tableContainerRef}>
        <CustomTable
          dataSource={sortedLoads}
          columns={columns}
          rowKey="loadId"
          loading={loading}
          onChange={handleTableChange}
          locale={{ emptyText }}
          pagination={{
            current: currentPage,
            pageSize: pagination.pageSize,
            total: sortedLoads.length,
            showTotal: (total, [from, to]) =>
              t('tsShowingRange', { from, to, total }),
          }}
          scroll={{ x: 760 }}
          rowClassName={(record) =>
            drawerOpen && record.loadId === openLoadId
              ? 'transport-selected-row'
              : ''
          }
          onRow={(record) => ({
            tabIndex: 0,
            onClick: () => openLoadDetails(record),
            onKeyDown: (e) => {
              if (e.target !== e.currentTarget) return;
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                openLoadDetails(record);
              }
            },
          })}
        />
      </LoadTableContainer>

      <DrawerViewport>
        <DrawerSticky>
          <TransportLoadDrawer
            load={openLoad}
            open={drawerOpen && !!openLoad}
            width={DRAWER_WIDTH}
            onClose={closeDrawer}
            afterOpenChange={handleDrawerOpenChange}
          />
        </DrawerSticky>
      </DrawerViewport>
    </Card>
  );
};
